import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { OAuthService } from '../../../api/services/oauth.service';
import { ToastService } from '../../../services/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-oauth-login',
  templateUrl: './oauth-login.component.html',
  standalone: false,
})
export class OauthLoginComponent {
  loading = false;
  twoFactor = false;
  errorMessage: string | null = null;
  applicationName = 'the connected application';
  /**
   * Form used to authenticate
   */
  loginForm = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    password: new FormControl('', Validators.required),
    twoFactorCode: new FormControl<string>(''),
  });
  private clientId!: string;
  private redirectUri!: string;
  private codeChallenge!: string;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private oauthService: OAuthService,
    private toastService: ToastService,
  ) {
    // Get properties from query used to set up OAuth
    this.clientId = this.route.snapshot.queryParams['client_id'];
    this.redirectUri = this.route.snapshot.queryParams['redirect_uri'];
    this.codeChallenge = this.route.snapshot.queryParams['code_challenge'];

    if (!this.clientId || !this.redirectUri || !this.codeChallenge) {
      this.errorMessage =
        'This sign-in request is missing required OAuth details. Please close this window and try linking the account again.';
      return;
    }

    // Client details are display-only; never block the OAuth login form on this lookup.
    this.oauthService
      .clientsGet$Response({ clientId: this.clientId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (clientDetails) => {
          this.applicationName =
            clientDetails.body.name || this.applicationName;
        },
        error: () => undefined,
      });
  }

  async login() {
    if (this.loading || this.errorMessage) {
      return;
    }

    const twoFactorAuthenticationCode = this.loginForm.value.twoFactorCode
      ? this.loginForm.value.twoFactorCode
      : undefined;

    this.loading = true;

    this.oauthService
      .authenticatePost$Response({
        body: {
          email: this.loginForm.value.email!,
          password: this.loginForm.value.password!,
          twoFactorAuthenticationCode: twoFactorAuthenticationCode,
          redirectUri: this.redirectUri,
          clientId: this.clientId,
          codeChallenge: this.codeChallenge,
        },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: async (res) => {
          this.redirectToBackendValidatedUrl(res.body.redirectUri);
        },
        error: (err) => {
          if (err.status === 422) {
            // If we get 422 (unprocessable entity) response, we also need a 2FA token
            this.twoFactor = true;
            this.toastService.info('2FA Authentication Code Required');
          } else {
            // Otherwise, reset the form as the entered credentials are incorrect
            this.twoFactor = false;
            this.loginForm.reset();
            this.toastService.error('Login Credentials Invalid');
          }
        },
      });
  }

  protected redirectToBackendValidatedUrl(redirectUri: string): void {
    // OAuth redirect trust boundary is backend-owned. Authenticate rejects
    // unregistered redirect URIs before returning this value.
    window.location.href = redirectUri;
  }
}
